
import { effect, HostListener, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, NavigationStart, Route, Router } from "@angular/router";
import { BehaviorSubject, distinctUntilChanged, filter, noop, tap } from "rxjs";
import { Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { BrowserService } from "projects/shared/services/browser.service";
import { Store } from "@ngrx/store";
import { SelectRegionalLanguage } from "../../layout/navbar/components/navbar-header/partials/nav-regional/store/regional-setting.selectors";
import { TranslateService } from "@ngx-translate/core";
import { RegionalActions } from "../../layout/navbar/components/navbar-header/partials/nav-regional/store/regional-setting.actions";

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  arabic = new BehaviorSubject<boolean>(false);
  selectedCountry = new BehaviorSubject<string>(null!);
  onLoadComplete = new BehaviorSubject<boolean>(true);

  language$ = this._store.selectSignal(SelectRegionalLanguage);

  constructor(
    private _router: Router,
    private _location: Location,
    private _browserService: BrowserService,
    private _store: Store,
    private _translateService: TranslateService
  ) {
    this.getLanguageFromRoute();
    this.routeSubscription();
    effect(() => {
      const currentLang = this.language$();
      if (currentLang) {
        this._translateService.use(currentLang);
        this.arabic.next(currentLang === 'ar');
        this.changeLangToArabic(this.getCurrentPath());
      }
    });
  }

  getCurrentPath(): string {
    const locPath = this._location.path();
    if (locPath) return locPath;
    const routerUrl = this._router.url;
    if (routerUrl && routerUrl !== '/') return routerUrl;
    const browserLoc = this._browserService.getLocation();
    if (browserLoc?.pathname) return browserLoc.pathname;
    return '';
  }

  getLanguageFromRoute() {
    const path = this.getCurrentPath();
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const firstSegment = cleanPath.split('/')[0]?.split('?')[0];
    const lang = (firstSegment === 'ar' || firstSegment === 'en') ? firstSegment : 'en';
    this.arabic.next(lang === 'ar');
    this._store.dispatch(RegionalActions.changeLanguage({ language: lang }));
  }

  routeSubscription() {
    this._router.events.pipe(
      filter((res): res is NavigationEnd => res instanceof NavigationEnd),
      distinctUntilChanged(),
      tap(res => {
        const url = res.urlAfterRedirects || res.url;
        const cleanPath = (url || '').startsWith('/') ? url.slice(1) : url;
        const firstSegment = cleanPath.split('/')[0]?.split('?')[0];
        if (firstSegment === 'ar' || firstSegment === 'en') {
          if (this.language$() !== firstSegment) {
            this._store.dispatch(RegionalActions.changeLanguage({ language: firstSegment }));
          }
          this.arabic.next(firstSegment === 'ar');
        }
        this.changeLangToArabic(url);
      })
    ).subscribe();
  }

  changeLangToArabic(url: string) {
    if (!url) return;
    const currentLang = this.language$();
    if (currentLang && currentLang !== 'null') {
      try {
        if (currentLang === 'ar') {
          this.arabic.next(true);
          const newUrl = this.editRoute(url, true);
          if (newUrl === this._location.path()) return;
          if (this._browserService.isBrowser()) {
            this._location.replaceState(newUrl);
          }
        } else {
          this.arabic.next(false);
          const newUrl = this.editRoute(url, false);
          if (newUrl === this._location.path()) return;
          if (this._browserService.isBrowser()) {
            this._location.replaceState(newUrl);
          }
        }
      } catch (e) {
        const newUrl = this.editRoute(url, false);
        if (newUrl === this._location.path()) return;
        if (this._browserService.isBrowser()) {
          this._location.replaceState(newUrl);
        }
      }
    }
  }

  private editRoute(route: string, ar: boolean) {
    let u: any = (route || '').split('/');
    if (u.length > 1) {
      u[1] = ar ? 'ar' : 'en';
    }
    return u.join('/').split(',').toString();
  }
}


